import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import type { UncodedDisclosure, UncodedPlatform, UncodedReason } from '../../types/gotypes'
import { platformLabel } from './platformFilter'

export interface UncodedDisclosureModalProps {
  open: boolean
  onClose: () => void
  disclosure?: UncodedDisclosure
}

/**
 * "Which comments are in these numbers."
 *
 * 🔴 IT STATES THREE FACTS AND SUMS NOTHING. There is a fourth population —
 * comments we imported that hold no coding row and state no reason — which we
 * cannot explain and therefore do not report. Any total shown here would fail
 * to reconcile against the imported count the moment that population is
 * non-empty, and it is non-empty today. So each line stands on its own terms:
 * "N are in these charts", "M had no text", "K could not be analysed" — three
 * separate statements, never an equation.
 *
 * The two exclusions render as SEPARATE GROUPS. "We hold the comment and there
 * was nothing in it to analyse" is a fact about Sky's data; "we tried and the
 * model could not read it" is a fact about our processing. Putting them under
 * one heading would invite the reader to treat them as one kind of loss.
 *
 * A category with a zero count is omitted entirely rather than shown as 0 —
 * "0 comments had no text" is noise on a panel whose whole job is to be worth
 * reading.
 */
export const UncodedDisclosureModal: React.FC<UncodedDisclosureModalProps> = ({
  open,
  onClose,
  disclosure,
}) => {
  const notCodeable = sumReasons(disclosure?.notCodeable)
  const failed = sumReasons(disclosure?.failed)
  const coded = disclosure?.coded ?? 0
  const hasDeleted = (disclosure?.notCodeable ?? []).some(r => r.reason === 'deleted_at_source' && (r.count ?? 0) > 0)
  const reasonChips = notCodeableReasonChips(disclosure?.notCodeable)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="uncoded-disclosure-title"
    >
      <DialogTitle id="uncoded-disclosure-title" sx={{ fontWeight: 600 }}>
        Which comments are in these numbers
      </DialogTitle>
      <DialogContent data-testid="uncoded-disclosure-content">
        {coded > 0 && (
          <Statement
            testId="uncoded-disclosure-coded"
            count={coded}
            label="in these charts"
            detail="Analysed for sentiment, emotion and every coded variable on this page."
          />
        )}
        {notCodeable > 0 && (
          <Statement
            testId="uncoded-disclosure-notcodeable"
            count={notCodeable}
            label="imported with no text to analyse"
            detail={
              hasDeleted
                ? 'We hold these comments and count them as engagement, but there are no words to analyse: ' +
                  'the platform returned none, or the comment was deleted there before we analysed it. ' +
                  'They are in no chart on this page. A comment deleted after we analysed it keeps its codes ' +
                  'and is counted in the charts.'
                : 'We hold these comments and count them as engagement, but the platform ' +
                  'returned no words for them — so they are in no chart on this page.'
            }
            reasons={reasonChips}
            breakdown={mergePlatforms(disclosure?.notCodeable)}
          />
        )}
        {failed > 0 && (
          <Statement
            testId="uncoded-disclosure-failed"
            count={failed}
            label="could not be analysed"
            detail={detailForFailures(disclosure?.failed)}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="info" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface StatementProps {
  count: number
  label: string
  detail: string
  testId: string
  /** Optional per-platform decomposition of `count`, biggest first. */
  breakdown?: UncodedPlatform[]
  /** Optional decomposition of `count` by reason, drawn above the platforms. */
  reasons?: { reason: string; label: string; count: number }[]
}

const Statement: React.FC<StatementProps> = ({ count, label, detail, testId, breakdown, reasons }) => (
  <Box data-testid={testId} sx={{ mb: 2.5 }}>
    <Typography variant="body1" sx={{ fontWeight: 600, color: 'grey.900' }}>
      {count.toLocaleString()} {label}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
      {detail}
    </Typography>
    {reasons !== undefined && reasons.length > 0 && (
      <Box
        data-testid={`${testId}-reasons`}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}
      >
        {reasons.map((r) => (
          <Typography
            key={r.reason}
            variant="caption"
            sx={{ color: 'text.secondary', bgcolor: 'action.hover', borderRadius: 1, px: 0.75, py: 0.25 }}
          >
            {r.label} <strong>{r.count.toLocaleString()}</strong>
          </Typography>
        ))}
      </Box>
    )}
    {breakdown !== undefined && breakdown.length > 0 && (
      <Box
        data-testid={`${testId}-platforms`}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}
      >
        {breakdown.map((p) => (
          <Typography
            key={p.platform || '(unknown)'}
            variant="caption"
            sx={{
              color: 'text.secondary',
              bgcolor: 'action.hover',
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
            }}
          >
            {p.platform ? platformLabel(p.platform) : 'Platform not recorded'}{' '}
            <strong>{p.count.toLocaleString()}</strong>
          </Typography>
        ))}
      </Box>
    )}
  </Box>
)

/**
 * Sums the per-platform breakdowns of every reason on a line into the ONE list
 * the line shows, because the line itself is a sum of its reasons.
 *
 * 🔴 IT MUST STAY A SUM OF THE SAME REASONS THE HEADLINE SUMS. `sumReasons`
 * adds every reason's count; if this added only the first reason's platforms
 * the chips would read short of the number directly above them, on a panel
 * whose entire job is to be trusted about numbers.
 *
 * Ordering is re-derived here rather than inherited: the server sorts within a
 * reason, and merging two sorted lists does not give a sorted list.
 */
function mergePlatforms(reasons?: UncodedReason[]): UncodedPlatform[] {
  const totals = new Map<string, number>()
  for (const reason of reasons ?? []) {
    for (const p of reason.platforms ?? []) {
      totals.set(p.platform, (totals.get(p.platform) ?? 0) + (p.count ?? 0))
    }
  }
  return [...totals.entries()]
    .map(([platform, count]) => ({ platform, count }))
    .sort((a, b) => b.count - a.count || a.platform.localeCompare(b.platform))
}

/**
 * Names the failure kinds actually present rather than asserting both. The
 * vocabulary is the coding states (`failed`, `content_filtered`), which mean
 * different things to a reader and so are not collapsed into one sentence.
 */
function detailForFailures(reasons?: UncodedReason[]): string {
  const kinds = (reasons ?? []).filter(r => (r.count ?? 0) > 0).map(r => r.reason)
  if (kinds.includes('content_filtered') && kinds.length === 1) {
    return 'The model’s content filter declined to process these comments.'
  }
  if (kinds.includes('content_filtered')) {
    return 'Some failed to process; the rest were declined by the model’s content filter.'
  }
  return 'These comments failed to process and are in no chart on this page.'
}

/** What a not-codeable reason is called on screen. Add the label with the reason. */
export const NOT_CODEABLE_REASON_LABELS: Record<string, string> = {
  no_text: 'No text',
  deleted_at_source: 'Deleted on the platform',
}

/**
 * Per-reason counts, shown only when a reason other than `no_text` is present:
 * a line of plain no-text comments needs no breakdown, and a deleted comment
 * must not read as one the platform never gave us words for.
 */
function notCodeableReasonChips(reasons?: UncodedReason[]): { reason: string; label: string; count: number }[] {
  const present = (reasons ?? []).filter(r => (r.count ?? 0) > 0)
  if (!present.some(r => r.reason !== 'no_text')) return []
  return present
    .map(r => ({ reason: r.reason, label: NOT_CODEABLE_REASON_LABELS[r.reason] ?? r.reason.replace(/_/g, ' '), count: r.count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

function sumReasons(reasons?: UncodedReason[]): number {
  if (!reasons) return 0
  return reasons.reduce((total, r) => total + (r.count ?? 0), 0)
}

export default UncodedDisclosureModal
